import { isNativePlatform } from "@/native/platform";
import type React from "react";

const WORKSPACE_ACTIVATING_POINTER_TYPE = "workspaceActivatingPointerType";

type MouseEventWithPointerType = MouseEvent & {
  [WORKSPACE_ACTIVATING_POINTER_TYPE]?: PointerEvent["pointerType"];
};

function getActivatingPointerType(
  e: React.MouseEvent | MouseEvent,
): PointerEvent["pointerType"] | undefined {
  const native = "nativeEvent" in e ? e.nativeEvent : e;
  return (native as MouseEventWithPointerType)[
    WORKSPACE_ACTIVATING_POINTER_TYPE
  ];
}

/** Attach the pointer type from the row's pointerdown to its click. */
export function stampActivatingPointerType(
  e: React.MouseEvent,
  pointerType: PointerEvent["pointerType"] | null,
): void {
  if (pointerType == null) return;
  (e.nativeEvent as MouseEventWithPointerType)[
    WORKSPACE_ACTIVATING_POINTER_TYPE
  ] = pointerType;
}

export function hasWorkspaceSelectionModifier(
  e: React.MouseEvent | MouseEvent,
): boolean {
  return e.shiftKey || e.metaKey || e.ctrlKey || e.altKey;
}

/**
 * Touch-primary surfaces (phones, tablets, Chrome device emulation).
 * `dblclick` is unreliable here, so an already-selected item opens on
 * the next unmodified tap instead of requiring double-click.
 */
export function isTouchPrimaryEnvironment(): boolean {
  if (typeof window === "undefined") return false;
  // jsdom has no matchMedia; treat it as desktop-like.
  if (typeof window.matchMedia !== "function") return false;
  if (window.matchMedia("(pointer: coarse)").matches) return true;
  if (window.matchMedia("(hover: none)").matches) return true;
  // Responsive DevTools + touch simulation can still report a fine pointer;
  // treat narrow touch-capable viewports as touch-primary too.
  if (
    navigator.maxTouchPoints > 0 &&
    window.matchMedia("(max-width: 1024px)").matches
  ) {
    return true;
  }
  return false;
}

function isTouchLikeActivation(e: React.MouseEvent | MouseEvent): boolean {
  if (getActivatingPointerType(e) === "touch") return true;
  return isTouchPrimaryEnvironment();
}

/**
 * Whether an unmodified tap should open the item outright.
 *
 * In the native app a single tap opens — that is the phone-native contract,
 * and the mobile surfaces have no use for tap-to-select.
 *
 * Everywhere else — including mobile web, tablets, and touchscreen laptops —
 * the web behaviour is preserved exactly: the first tap selects and only a tap
 * on an already-selected item opens it. Gating on the platform rather than the
 * pointer type is what keeps the browser surface byte-for-byte unchanged.
 */
export function shouldOpenWorkspaceItemOnActivate(
  isSelected: boolean,
  e: React.MouseEvent | MouseEvent,
): boolean {
  if (hasWorkspaceSelectionModifier(e)) return false;
  if (!isTouchLikeActivation(e)) return false;
  return isNativePlatform() || isSelected;
}

export function handleWorkspaceItemActivate(params: {
  isSelected: boolean;
  e: React.MouseEvent | MouseEvent;
  onOpen: () => void;
  onSelect: () => void;
}): void {
  if (shouldOpenWorkspaceItemOnActivate(params.isSelected, params.e)) {
    params.onOpen();
    return;
  }
  params.onSelect();
}
